import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, TextField, Autocomplete, Chip } from "@mui/material";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { useNavigate, useParams } from "react-router-dom";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff"
import { subscriptionService } from "../../../api";
import AuthService from "../../../api/AuthService";
import { useAlert } from "../../../context/AlertContext";
import styles from "./style";
import SubAdminPermissionsComponent from "../SubAdminPermissions";
import LoaderHelper from "../../../utils/Loader/LoaderHelper";

const normalizeListResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.subscriptions)) return response.data.subscriptions;
  if (Array.isArray(response?.subscriptions)) return response.subscriptions;
  if (Array.isArray(response?.result)) return response.result;
  return [];
};

const getCompanyCountry = (item = {}) => {
  const company = item?.companyId || item?.companyData || item;
  return String(company?.country || item?.country || "").trim();
};

const getUniqueCountries = (countries = []) => {
  const countryMap = new Map();

  countries.forEach((country) => {
    const trimmedCountry = String(country || "").trim();
    const key = trimmedCountry.toLowerCase();
    if (trimmedCountry && !countryMap.has(key)) {
      countryMap.set(key, trimmedCountry);
    }
  });

  return Array.from(countryMap.values());
};

const CreateEditSubAdminPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showAlert } = useAlert();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phone: "",
    countryCode: "+27",
    assignedCountries: [],
    assignedCompanies: [],
    company: "",
  });

  const [countryPermissions, setCountryPermissions] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [companiesList, setCompaniesList] = useState([]);
  const [ownerCountries, setOwnerCountries] = useState([]);
  const [initialFormData, setInitialFormData] = useState(null);
  const [hasChanges, setHasChanges] = useState(!isEditMode);
  const [countryData, setCountryData] = useState([]);
  const [showPassword, setShowPassword] = useState(false);

  // Fetch companies list on mount
  useEffect(() => {
    fetchCompaniesList();
    getSelectedCountries();
  }, []);

  const fetchCompaniesList = async () => {
    try {
      const result = await AuthService.getCompantList();
      if (result?.success && Array.isArray(result?.data)) {
        setCompaniesList(result.data);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const getSelectedCountries = async () => {
    LoaderHelper.loaderStatus(true);

    try {
      const result = await AuthService.getSelectedCountries();
      if (result?.success) {
        const selectedData = result?.data?.countrySelections?.[0]
        setCountryData(selectedData?.countries)
        LoaderHelper.loaderStatus(false);
      } else {

        LoaderHelper.loaderStatus(false);
      }
    } catch (error) {

      LoaderHelper.loaderStatus(false);
    }
  };

  const fetchOwnerDashboardCountries = async () => {
    try {
      const result = await subscriptionService.list({ page: 1, limit: 1000 });
      const subscriptions = normalizeListResponse(result);
      const countries = subscriptions
        .filter((subscription) => subscription?.adminId?.isDeleted !== true)
        .map(getCompanyCountry)
        .filter(Boolean);

      setOwnerCountries(getUniqueCountries(countries));
    } catch (error) {
      console.error("Error fetching owner dashboard countries:", error);
      setOwnerCountries([]);
    }
  };

  // Fetch Sub Admin data if in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      fetchSubAdminData();
    }
  }, [id, isEditMode]);

  // Track form changes
  useEffect(() => {
    if (isEditMode && initialFormData) {
      const changed = checkFormChanges();
      setHasChanges(changed);
    } else {
      setHasChanges(!isEditMode);
    }
  }, [formData, countryPermissions, initialFormData, isEditMode]);

  const fetchSubAdminData = async () => {
    try {
      setFetching(true);
      const response = await AuthService.getSubAdminById(id);

      if (response?.data) {
        const data = response.data;
        const admin = data.admin || {};
        const newFormData = {
          firstName: admin.firstName || "",
          lastName: admin.lastName || "",
          email: admin.email || data.email || "",
          password: "",
          phone: admin.phone || "",
          countryCode: admin.countryCode || "+27",
          assignedCountries: data.assignedCountries || [],
          assignedCompanies: data.assignedCompanies || [],
          company: data.company || "",
        };

        setFormData(newFormData);
        setInitialFormData(newFormData);
        setCountryPermissions(data.countryPermissions || {});
      }
    } catch (error) {
      showAlert("Failed to fetch Sub Admin data", "error");
      console.error("Error fetching Sub Admin:", error);
    } finally {
      setFetching(false);
    }
  };

  const checkFormChanges = () => {
    if (!initialFormData) return false;

    const normalize = (val) => String(val || "").trim();

    return (
      normalize(formData.firstName) !== normalize(initialFormData.firstName) ||
      normalize(formData.lastName) !== normalize(initialFormData.lastName) ||
      normalize(formData.email) !== normalize(initialFormData.email) ||
      normalize(formData.phone) !== normalize(initialFormData.phone) ||
      normalize(formData.countryCode) !== normalize(initialFormData.countryCode) ||
      normalize(formData.company) !== normalize(initialFormData.company) ||
      JSON.stringify(formData.assignedCountries) !==
      JSON.stringify(initialFormData.assignedCountries) ||
      JSON.stringify(formData.assignedCompanies) !==
      JSON.stringify(initialFormData.assignedCompanies) ||
      JSON.stringify(countryPermissions) !== JSON.stringify({})
    );
  };

  const validateForm = () => {
    const newErrors = {};

    // Basic Fields
    if (!formData.firstName?.trim()) newErrors.firstName = "First Name is required";
    if (!formData.lastName?.trim()) newErrors.lastName = "Last Name is required";
    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!isEditMode && !formData.password?.trim()) {
      newErrors.password = "Password is required";
    }

    // Phone
    if (!String(formData.phone || "").trim()) {
      newErrors.phone = "Phone number is required";
    }

    // ==================== COMPANY / COUNTRY VALIDATION ====================
    const hasCompany = Array.isArray(formData.assignedCompanies) && formData.assignedCompanies.length > 0;
    const hasCountry = Array.isArray(formData.assignedCountries) && formData.assignedCountries.length > 0;

    if (!hasCompany && !hasCountry) {
      newErrors.assignedCompanies = "Please select at least one Company or one Country";
      newErrors.assignedCountries = "Please select at least one Company or one Country";
    }
    // =====================================================================

    // Permissions Validation
    const hasPermissions = Object.keys(countryPermissions).some((country) =>
      Object.keys(countryPermissions[country] || {}).some(
        (module) =>
          countryPermissions[country][module]?.view === true ||
          countryPermissions[country][module]?.edit === true
      )
    );

    if (!hasPermissions) {
      newErrors.permissions = "At least one permission must be granted";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    const isValid = validateForm();

    try {
      setLoading(true);

      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: String(formData.phone || "").trim(),
        countryCode: formData.countryCode,
        assignedCountries: formData.assignedCountries || [],
        assignedCompanies: formData.assignedCompanies || [],
        company: formData.company,
        countryPermissions,
      };

      if (!isEditMode) {
        payload.password = formData.password;
      } else if (formData.password?.trim()) {
        payload.password = formData.password.trim();
      }

      const response = isEditMode
        ? await AuthService.updateSubAdmin(id, payload)
        : await AuthService.createSubAdmin(payload);

      // Handle API response with success false
      if (!response?.success) {
        showAlert(
          response?.message || "Failed to save Sub Admin",
          "error"
        );
        return;
      }

      showAlert(
        isEditMode
          ? "Sub Admin updated successfully"
          : "Sub Admin created successfully",
        "success"
      );

      navigate("/dashboard/sub-admin");

    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to save Sub Admin";

      showAlert(errorMsg, "error");

      console.error("Error saving Sub Admin:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear field error
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }

    // Clear company/country error when user selects any one
    if (field === "assignedCompanies" || field === "assignedCountries") {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated.assignedCompanies;
        delete updated.assignedCountries;
        return updated;
      });
    }
  };

  const handlePermissionsChange = (updatedPermissions) => {
    setCountryPermissions(updatedPermissions);
    if (errors.permissions) {
      setErrors((prev) => ({
        ...prev,
        permissions: "",
      }));
    }
  };
  const handleCancel = () => {
    navigate("/dashboard/sub-admin");
  };

  if (fetching) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Grid container sx={styles.container}>
      <Grid sx={styles.headerContainer}>
        <Box>
          <Typography variant="body1" sx={styles.title}>
            {isEditMode ? "Edit Sub Admin" : "Create Sub Admin"}
          </Typography>
        </Box>
        <Box sx={styles.subheadingBox}>
          <Typography variant="body1" sx={styles.customHeading}>
            Custom
          </Typography>
          <Box sx={styles.line}></Box>
        </Box>
      </Grid>

      <Grid container sx={styles.section}>
        <Grid item xs={12}>
          <Box sx={styles.cardHeader}>
            <Box sx={styles.headerLine} />
            <Typography variant="body1" sx={styles.sectionTitle}>
              Basic Information
            </Typography>
          </Box>
        </Grid>

        <Grid container spacing={2} sx={styles.formGrid}>
          {/* First Name */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              value={formData.firstName}
              onChange={(e) => handleChange("firstName", e.target.value)}
              error={!!errors.firstName}
              helperText={errors.firstName}
              placeholder="First Name"
              size="small"
              sx={styles.textField}
            />
          </Grid>

          {/* Last Name */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              value={formData.lastName}
              onChange={(e) => handleChange("lastName", e.target.value)}
              error={!!errors.lastName}
              helperText={errors.lastName}
              placeholder="Last Name"
              size="small"
              sx={styles.textField}
            />
          </Grid>

          {/* Email */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              type="email"
              autoComplete="off"
              name="subadmin-email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              placeholder="Email"
              size="small"
              sx={styles.textField}
            />
          </Grid>

          {/* Password */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              name="subadmin-password"
              autoComplete="new-password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={(e) => handleChange("password", e.target.value)}
              error={!!errors.password}
              helperText={errors.password}
              placeholder={isEditMode ? "Password (optional)" : "Password"}
              size="small"
              sx={styles.textField}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Phone Number */}
          <Grid item xs={12} sm={6} md={4}>
            <Box>
              <PhoneInput
                country="za"
                value={String(formData.phone || "")}
                onChange={(value, country) => {
                  handleChange("phone", value);
                  handleChange("countryCode", `+${country.dialCode}`);
                }}
                placeholder="Phone Number"
                inputStyle={{
                  width: "100%",
                  height: "40px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  borderColor: "#DDDDDD",
                }}
              />
              {errors.phone && (
                <Typography sx={styles.errorText}>{errors.phone}</Typography>
              )}
            </Box>
          </Grid>

          {/* Assigned Countries */}
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              multiple
              options={countryData || []}
              value={formData.assignedCountries || []}
              onChange={(e, value) =>
                handleChange("assignedCountries", value)
              }
              isOptionEqualToValue={(option, value) => option === value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select countries"
                  error={!!errors.assignedCountries}
                  helperText={errors.assignedCountries}
                  size="small"
                  sx={styles.textField}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={index}
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
              sx={styles.autoComplete}
            />
          </Grid>
          {/* Assigned Companies */}
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              multiple
              options={companiesList}
              getOptionLabel={(option) => option?.companyName || ""}
              value={companiesList.filter((c) =>
                formData.assignedCompanies?.includes(c._id)
              )}
              onChange={(e, value) =>
                handleChange(
                  "assignedCompanies",
                  value?.map((c) => c._id) || []
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select companies"
                  error={!!errors.assignedCompanies}
                  helperText={errors.assignedCompanies}
                  size="small"
                  sx={styles.textField}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.companyName}
                    {...getTagProps({ index })}
                    key={index}
                  />
                ))
              }
              sx={styles.autoComplete}
            />
          </Grid>
        </Grid>
          <SubAdminPermissionsComponent
            selectedCountries={formData.assignedCountries}
            assignedCompanies={formData.assignedCompanies}
            companiesList={companiesList}
            countryPermissions={countryPermissions}
            onPermissionsChange={handlePermissionsChange}
            onSave={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
            hasChanges={hasChanges}
          />
      </Grid>
    </Grid>
  );
};

export default CreateEditSubAdminPage;
